import { add, vn } from 'vend-number'
import { KLARNA_PAYMENT_TYPES } from '../../components/payments/integrated-payment/integrated-payment-types.constant'
import { Payment } from '../../models/payment'
import { Sale } from '../../models/sale'
import { CONFIRMED_STATUSES, SaleStatus } from '../../models/sale.constants'
import {
  hasOnlyOnePaymentForABP,
  hasPaymentsExcludingPaymentTypes,
  isABPAddedButNotPaid,
} from '../payments'
import { ReduxCurrentSale } from '../../state/slices/currentSale/currentSaleSlice'
import { isReturn } from '../sale'
import { containsGiftCard } from '../lineItem'
import { lineItemsAdapter } from '../../state/slices/currentSale/lineItemEntityAdapter'

export const KLARNA_EXCHANGE_NOT_POSSIBLE_MESSAGE = `Sorry, you're unable to exchange items paid for using Klarna. To continue, refund the
entire sale and start a new sale.`

/**
 * Determines if the given payment type id is a klarna payment type id.
 *
 * @param paymentTypeID The given payment type id
 *
 * @return True if the given payment type id is a klarna payment type id, false otherwise
 */
export const isKlarnaPaymentType = (paymentTypeID: number): boolean => {
  return KLARNA_PAYMENT_TYPES.includes(paymentTypeID)
}

/**
 * Determine if there are any Klarna or Klarna_** payments within a collection of payments.
 *
 * @method hasKlarnaPayments
 *
 * @param {Object} params
 *
 *        @param {Array<Payment>} params.payments
 *               Collection of payments.
 *
 * @return {Boolean} True if a Klarna payment exists in the collection, false otherwise.
 */
export const hasKlarnaPayments = ({
  payments = [],
}: {
  payments: Payment[]
}): boolean => {
  return payments.some(payment => {
    return isKlarnaPaymentType(payment.paymentType.type_id)
  })
}

/**
 * Determine if there are any Non-Klarna payments within a collection of payments.
 *
 * @method hasNonKlarnaPayments
 *
 * @param {Object} params
 *
 *        @param {Array<Payment>} params.payments
 *               Collection of payments.
 *
 * @return {Boolean} True if an Non-Klarna payment exists in the collection, false otherwise.
 */
const hasNonKlarnaPayments = ({
  payments = [],
}: {
  payments: Payment[]
}): boolean => {
  return hasPaymentsExcludingPaymentTypes({
    payments,
    paymentTypeIds: KLARNA_PAYMENT_TYPES,
  })
}

/**
 * Prevents inventory items from being added for returns whose original sale has Klarna payments.
 *
 * @method shouldPreventExchangeForKlarna
 *
 * @param {Sale} sale
 *        The sale to evaluate.
 *
 * @return {Boolean} True if an exchange is possible, false otherwise.
 */
export const shouldPreventExchangeForKlarna = (
  sale: Sale | ReduxCurrentSale
): boolean => {
  if (!isReturn(sale)) {
    return false
  }

  return hasKlarnaPayments({ payments: sale.originalSalePayments })
}

/**
 * Evaluates the current sale and determines if the Klarna payment option should be disabled.
 *
 * @method shouldDisableKlarnaPayment
 *
 * @param {Number} amountTendered
 * @param {Sale} sale
 *
 * @return {Boolean} True if the Klarna payment option should be disabled, false otherwise.
 */
export const shouldDisableKlarnaPayment = (
  amountTenderedNum: number | string,
  sale: Pick<
    ReduxCurrentSale,
    | 'finals'
    | 'payments'
    | 'payAccountBalance'
    | 'status'
    | 'lineItems'
    | 'originalSalePayments'
    | 'returnFor'
  >
): boolean => {
  const amountTendered = vn(amountTenderedNum)
  const totalBal = sale.finals.inclusivePrice

  // Disable if Account Balance Payment has added to sale but not paid
  if (isABPAddedButNotPaid(sale)) {
    return true
  }

  if (!amountTendered.eq(totalBal)) {
    return true
  }

  // Disable Klarna if the sale is a Layby or On Account
  if (CONFIRMED_STATUSES.includes(sale.status as SaleStatus)) {
    return true
  }

  // Disable if already paid by other payment type
  if (
    hasNonKlarnaPayments({ payments: sale.payments }) &&
    !hasOnlyOnePaymentForABP(sale)
    // Except the non Klarna payment is paid for Account Balance Payment only,
    // to enable Klarna payment for the rest sale price
  ) {
    return true
  }

  // Disable if the sale contains a gift card
  if (
    containsGiftCard(lineItemsAdapter.getSelectors().selectAll(sale.lineItems))
  ) {
    return true
  }

  if (isReturn(sale)) {
    // disable exchange for higher value item
    if (amountTendered.gt(0)) {
      return true
    }

    // disable if there are not any kind of Klarna payments on the original sale
    if (!hasKlarnaPayments({ payments: sale.originalSalePayments })) {
      return true
    }

    return vn(getKlarnaOutstandingBalance(sale)).lte(0)
  }

  // Don't allow Klarna of negative amounts or overpaying (i.e. requiring change) or already paid by Klarna
  return (
    amountTendered.lt(0) ||
    amountTendered.gt(totalBal) ||
    hasKlarnaPayments({ payments: sale.payments })
  )
}

/**
 * Calculate the outstanding balance by subtracting every Klarna payments from Klarna refunds.
 */
const getKlarnaOutstandingBalance = (
  sale: Pick<ReduxCurrentSale, 'payments' | 'originalSalePayments'>
) => {
  return getCurrentAndOriginalPayments(
    sale.payments,
    sale.originalSalePayments
  ).reduce((total, payment) => {
    return add(
      total,
      isKlarnaPaymentType(payment.paymentType.type_id) ? payment.amount : 0
    )
  }, 0)
}

/**
 * Return array of current and original payment from sale
 */
const getCurrentAndOriginalPayments = (
  payments: Payment[],
  originalSalePayments: Payment[]
) => {
  const currentAndOriginalPayments = payments.concat(originalSalePayments)
  return currentAndOriginalPayments
}
